import { EntryType, Octopus, OpenAI } from "@maximizer/core/shared/domain";
import { TextPromptClient } from 'langfuse-core';
import { PromptMetaExtended } from "../types/langfuse.types";
import { computed } from "@angular/core";
import { integrationTranslateConfiguration } from "@maximizer/core/shared/ui/config";

export enum ViewType {
  Report,
  DetailedReport,
  Chat,
  PrivacySecurityCenter,
}

export enum StreamingPhase {
  GettingContext,
  Anonymizing,
  ProcessingCustomDataSources,
  Thinking,
  WebSearch,
  GettingContextReadingRecords,
  GettingContextReadingDocuments,
  GettingContextFindingRelevant,
}

export enum IQChatFrom {
  User,
  Ai,
}

export type EnvironmentType = 'development' | 'qa' | 'staging' | 'production';
export type IQBoostPromptConfig =
  OpenAI.RequestConfiguration &
  LangfuseResponseExtension;

export const CODE_ADMIN = 'ADMIN';
export const DEFAULT_LANGUAGE = 'en';

export const DefaultDataFiltering: DataFiltering = {
  dueDate: Octopus.DateOptionId.LASTTWELVEMONTHS,
} as DataFiltering;

export const DefaultDataScope = {
  all: true,
  householdDetails: true,
  accountDetails: true,
  notes: true,
  emails: true,
  documents: true,
  calls: true,
  allInteractions: true,
  dataFiltering: DefaultDataFiltering,
} as DataScope;

export const DefaultDataProcessingFeatures: IQDataProcessingFeatures = {
  anonymized: true,
  useGlobalServers: false,
  useWebData: false,
};

export interface IQBoostCreateCRMAction {
  key: string;
  for?: string;
  displayText: string;
  title: string;
  description: string;
  action: string | undefined;
  date: string;
  openDialog: boolean;
}

export interface LangfuseResponseExtension {
  maximizer: {
    source: Array<{ type: string; order?: number }>;
    title: string | LangfuseResponseExtensionTitle;
    priviliges?: {
      create: boolean;
      references: boolean;
    },
    systemType?: string;
    suggestedPrompts?: LangfuseResponseExtensionSuggestedPrompt[];
    smartSearch?: LangfuseResponseExtensionSmartSearch;
    hiddenFromDatabases?: Array<string>;
  } 
    & LangfuseModelConfigOverride
    & LangfuseModelConfigDataSource;
  variables?: Record<string, LangfuseResponseExtensionVariable>;
}

export interface LangfuseResponseExtensionVariable {
  value?: string;
  translationValue?: LangfuseResponseExtensionTranslation;
}

export interface LangfuseResponseExtensionSmartSearch {
  min_weight: number;
}

export interface LangfuseResponseExtensionSuggestedPrompt {
  text: string;
  type?: unknown;
}

export interface LangfuseResponseExtensionTitle {
  value: string | LangfuseResponseExtensionTranslation;
  category: {
    value: string | LangfuseResponseExtensionTranslation;
    iconStyle: string;
  }
}

export interface LangfuseResponseExtensionTranslation {
  [key: string]: string;
}

export interface LangfuseResponseExtensionSource {
  type: string;
  order?: number;
}

export interface LangfuseModelConfigOverride {
  globalModelConfigOverride?: {
    model: string;
    temperature: number;
    max_tokens: number;
  };
}

export interface LangfuseModelConfigDataSource {
  data_source?: {
    data_scope: LangfuseModelConfigDataScope,
  }
}

export interface LangfuseModelConfigDataScope {
  address_book: boolean;
  opportunity: boolean;
  notes: boolean;
  emails: boolean;
  documents: boolean;
  calls: boolean;
  all_interactions: boolean;
}

export interface DataScope {
  all: boolean;
  householdDetails: boolean;
  accountDetails: boolean;
  notes: boolean;
  emails: boolean;
  documents: boolean;
  calls: boolean;
  allInteractions: boolean;
  dataFiltering?: DataFiltering;
}

export interface DataScopeRequest {
  addressBook: boolean;
  opportunity: boolean;
}

export interface DataFiltering {
  dueDate: Octopus.DateOptionId;
}

export interface AskAiInput {
  key?: string;
  type?: EntryType;
  userPrompt?: string;
  conversationId?: string;
  invalidateCache?: boolean;
}

export interface IQBoostContextUserMessages {
  context: OpenAI.GPTMessage[];
  user: OpenAI.GPTMessage[];
}

export interface IQBoostConversationMessages extends IQBoostContextUserMessages {
  system: OpenAI.GPTMessage[];
}

export interface ScopeChangeRequest {
  useAllHouseholds: boolean;
}
export interface RequestApiPromptMeta {
  promptMeta: PromptMetaExtended;
  askAi: boolean;
}

export interface ResponseTextPrompt {
  textPromptClient: TextPromptClient;
  request: RequestApiPromptMeta;
}

export interface IQChat {
  content: string;
  from: IQChatFrom;
}

export interface IQChatGroup {
  chatHistory: IQChat[];
  id: string;
}

export interface IQAiResponse {
  summary: string;
}

export interface IQDataProcessingFeatures {
  anonymized: boolean;
  useGlobalServers: boolean;
  useWebData: boolean;
}

export interface IQBoostSavedConfigurationUser {
  wasFirstTimeOpenedPrivacySecurityCenter: boolean;
  dataProcessingFeatures: IQDataProcessingFeatures;
  dataScope: DataScope;
  recentUserPrompts: string[];
}

export interface IQBoostSavedConfigurationAdmin {
  dataProcessingFeatures: IQDataProcessingFeatures;
}

export interface IQDataProcessingFeaturesGroup {
  admin: IQBoostSavedConfigurationAdmin;
  user: IQBoostSavedConfigurationUser;
  isAdmin: boolean;
}

export class LanguageHelper {
  static readonly language = computed(() => {
    return (top?.maximizerIdentity?.language
      ?? integrationTranslateConfiguration.fallbackLang
      ?? DEFAULT_LANGUAGE).toLowerCase();
  });
}

export interface SmartSearchResponse extends Octopus.Response {
  Data?: {
    timeline: Octopus.Timeline[];
    addressBook: Octopus.Entity[];
    opportunity: Octopus.Entity[];
    documents: DocumentContentResponse[];
  };
}

export interface DocumentContentResponse {
  Key: string;
  FileName: string;
  Content: string;
}
